import { useMemo } from 'react'
import { CellProps } from 'react-table'
import { formatDateTime } from '_metronic/helpers'
import { TFunction, useT } from '_metronic/i18n/Metronici18n'
import {
  PlainHeader,
  StackedCell,
  TableColumn,
  UseQueryRequestHook,
  sortableColumn,
} from 'app/components'
import { SncrTipoReceitaDot } from '../../../components/SncrTipoReceita'
import { ROTULO_TIPO_RECEITA_SNCR, SncrBloco } from '../../../core/_models'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { SncrBlocoActionsCell } from './SncrBlocoActionsCell'
import { SncrSaldoCell } from './SncrSaldoCell'

/**
 * O cabeçalho ordenável é tipado sobre `QueryState` sem `params`; só lê/escreve ordenação e
 * paginação, nunca a empresa. Ordenar preserva o filtro: `initialQueryState` não tem `params`.
 */
const useQueryRequestOrdenacao: UseQueryRequestHook = () => {
  const {
    state: { params, ...state },
    updateState,
  } = useQueryRequest()
  return { state, updateState }
}

/** Larguras fixas somam ~780px; a faixa divide o resto, então a tabela cabe sem rolagem. */
export const sncrBlocosColumns = (t: TFunction): ReadonlyArray<TableColumn<SncrBloco>> => [
  sortableColumn<SncrBloco>({
    id: 'tipo',
    cellWidth: '220px',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'tipo', label: t('sncr.lista.col_tipo') }],
    Cell: ({ row }: CellProps<SncrBloco>) => (
      <StackedCell
        primary={<SncrTipoReceitaDot tipo={row.original.tipo} />}
        secondary={ROTULO_TIPO_RECEITA_SNCR[row.original.tipo]}
      />
    ),
  }),
  sortableColumn<SncrBloco>({
    id: 'numeroInicio',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'numeroInicio', label: t('sncr.lista.col_faixa') }],
    Cell: ({ row }: CellProps<SncrBloco>) => (
      <StackedCell tabular primary={`${row.original.numeroInicio} – ${row.original.numeroFim}`} />
    ),
  }),
  {
    Header: () => <PlainHeader>{t('sncr.lista.col_quantidade')}</PlainHeader>,
    id: 'quantidade',
    cellWidth: '120px',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<SncrBloco>) => (
      <StackedCell tabular primary={row.original.quantidade} />
    ),
  },
  {
    // saldo é derivado (fim - próximo + 1), não é coluna do banco: sem ordenação.
    Header: () => <PlainHeader>{t('sncr.lista.col_saldo')}</PlainHeader>,
    id: 'saldo',
    cellWidth: '180px',
    Cell: ({ row }: CellProps<SncrBloco>) => <SncrSaldoCell bloco={row.original} />,
  },
  sortableColumn<SncrBloco>({
    id: 'dataRetirada',
    cellWidth: '170px',
    hideOnMobile: true,
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'dataRetirada', label: t('sncr.lista.col_retirado_em') }],
    Cell: ({ row }: CellProps<SncrBloco>) => (
      <StackedCell tabular primary={formatDateTime(row.original.dataRetirada)} />
    ),
  }),
  {
    Header: () => <PlainHeader>{t('sncr.lista.col_acoes')}</PlainHeader>,
    id: 'actions',
    cellWidth: '72px',
    align: 'end',
    Cell: ({ row }: CellProps<SncrBloco>) => <SncrBlocoActionsCell bloco={row.original} />,
  },
]

export const useSncrBlocosColumns = () => {
  const { t } = useT()
  return useMemo(() => sncrBlocosColumns(t), [t])
}
